import { apiRequest } from './api';
import { 
  createSessionEncryptionSetup, 
  decryptSessionKeys,
  encryptMessageForRecipient,
  encryptMessageForSender,
  validateMessageContent,
  generateMessageID
} from './privateChatCrypto';

// Get server public key
export const getServerPublicKey = async () => {
  try {
    const response = await fetch('/api/v1/auth/public-key');
    if (!response.ok) {
      throw new Error('Failed to fetch server public key');
    }
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get server public key');
    }
    return data.public_key;
  } catch (error) {
    throw error;
  }
};

/**
 * Private Chat API Functions
 * Handles all API calls for private chat functionality
 */

// Get all private chats for the current user
export const getPrivateChats = async (userKeys) => {
  try {
    const response = await apiRequest('/api/v1/private-chats', {
      method: 'GET'
    }, userKeys);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch private chats');
    }
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Create a new private chat session
export const createPrivateChat = async (receiverID, firstMessage, userPublicKeyBase64, userKeys) => {
  try {
    // Validate first message
    const validation = validateMessageContent(firstMessage);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Create session encryption setup
    const encryptionSetup = createSessionEncryptionSetup(userPublicKeyBase64);
    
    // Encode first message with base64 (no encryption needed)
    const firstMessageEncoded = btoa(unescape(encodeURIComponent(firstMessage)));
    
    const requestData = {
      receiver_id: receiverID,
      initiator_rsa_public_key: encryptionSetup.sessionRSAPublicKey,
      initiator_rsa_private_key_encrypted: encryptionSetup.sessionRSAPrivateKeyEncrypted,
      initiator_aes_key_encrypted: encryptionSetup.aesKeyEncrypted,
      first_message_encrypted: firstMessageEncoded
    };

    const response = await apiRequest('/api/v1/private-chats', {
      method: 'POST',
      body: JSON.stringify(requestData)
    }, userKeys);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to create private chat');
    }
    
    return {
      chat: response.data,
      encryptionSetup: encryptionSetup
    };
  } catch (error) {
    throw error;
  }
};

// Accept a pending private chat
export const acceptPrivateChat = async (chatID, userPublicKeyBase64, userKeys) => {
  try {
    // Create session encryption setup
    const encryptionSetup = createSessionEncryptionSetup(userPublicKeyBase64);
    
    const requestData = {
      receiver_rsa_public_key: encryptionSetup.sessionRSAPublicKey,
      receiver_rsa_private_key_encrypted: encryptionSetup.sessionRSAPrivateKeyEncrypted,
      receiver_aes_key_encrypted: encryptionSetup.aesKeyEncrypted
    };

    const response = await apiRequest(`/api/v1/private-chats/${chatID}/accept`, {
      method: 'PUT',
      body: JSON.stringify(requestData)
    }, userKeys);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to accept private chat');
    }
    
    return {
      success: true,
      encryptionSetup: encryptionSetup
    };
  } catch (error) {
    throw error;
  }
};

// Reject a pending private chat
export const rejectPrivateChat = async (chatID, userKeys) => {
  try {
    const response = await apiRequest(`/api/v1/private-chats/${chatID}/reject`, {
      method: 'PUT'
    }, userKeys);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to reject private chat');
    }
    
    return response;
  } catch (error) {
    throw error;
  }
};

// Get pending private chats
export const getPendingPrivateChats = async (userKeys) => {
  try {
    const response = await apiRequest('/api/v1/private-chats/pending', {
      method: 'GET'
    }, userKeys);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch pending private chats');
    }
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get private chat keys and message history
export const getPrivateChatKeys = async (chatID, userKeys) => {
  try {
    const response = await apiRequest(`/api/v1/private-chats/${chatID}/keys`, {
      method: 'GET'
    }, userKeys);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch chat keys');
    }
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Send a message in a private chat
export const sendPrivateMessage = async (chatID, message, otherUserPublicKey, userPublicKey, userKeys) => {
  try {
    // Validate message
    const validation = validateMessageContent(message);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Encrypt message for recipient
    const contentEncryptedForRecipient = await encryptMessageForRecipient(message, otherUserPublicKey);
    
    // Encrypt message for sender (backup)
    const contentEncryptedForSender = await encryptMessageForSender(message, userPublicKey);
    
    const requestData = {
      content_encrypted_for_recipient: contentEncryptedForRecipient,
      content_encrypted_for_sender: contentEncryptedForSender,
      message_type: 'text'
    };

    const response = await apiRequest(`/api/v1/private-chats/${chatID}/messages`, {
      method: 'POST',
      body: JSON.stringify(requestData)
    }, userKeys);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to send message');
    }
    
    return response;
  } catch (error) {
    throw error;
  }
};

// Get private chat messages
export const getPrivateChatMessages = async (chatID, userKeys, page = 1, limit = 50) => {
  try {
    const response = await apiRequest(`/api/v1/private-chats/${chatID}/messages?page=${page}&limit=${limit}`, {
      method: 'GET'
    }, userKeys);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch messages');
    }
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Clear private chat messages
export const clearPrivateChatMessages = async (chatID, userKeys) => {
  try {
    const response = await apiRequest(`/api/v1/private-chats/${chatID}/messages`, {
      method: 'DELETE'
    }, userKeys);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to clear messages');
    }
    
    return response;
  } catch (error) {
    throw error;
  }
};

// Delete private chat
export const deletePrivateChat = async (chatID, userKeys) => {
  try {
    const response = await apiRequest(`/api/v1/private-chats/${chatID}`, {
      method: 'DELETE'
    }, userKeys);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete private chat');
    }
    
    return response;
  } catch (error) {
    throw error;
  }
};

// Get user public key
export const getUserPublicKey = async (userID, userKeys) => {
  try {
    const response = await apiRequest(`/api/v1/private/user/${userID}/public-key`, {
      method: 'GET'
    }, userKeys);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch user public key');
    }
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Check if private chat exists between two users
export const checkPrivateChatExists = async (otherUserID, userKeys) => {
  try {
    const chats = await getPrivateChats(userKeys);
    const existingChat = chats.find(chat => chat.other_user_id === otherUserID);
    return existingChat || null;
  } catch (error) {
    return null;
  }
};

// Initialize private chat session
export const initializePrivateChatSession = async (chatID, userPrivateKeyPEM, userKeys) => {
  try {
    // Get chat keys
    const chatKeys = await getPrivateChatKeys(chatID, userKeys);
    
    // Decrypt session keys
    const sessionKeys = decryptSessionKeys(
      chatKeys.userRSAPrivateKeyEncrypted,
      chatKeys.userAESKeyEncrypted,
      userPrivateKeyPEM
    );
    
    return {
      chatKeys,
      sessionKeys,
      otherUserPublicKey: chatKeys.otherRSAPublicKey,
      userPublicKey: chatKeys.userRSAPublicKey
    };
  } catch (error) {
    throw error;
  }
};

// Format private chat list item
export const formatPrivateChatListItem = (chat) => {
  return {
    chatID: chat.chat_id,
    otherUserID: chat.other_user_id,
    otherUserPublicKey: chat.other_user_public_key,
    status: chat.status,
    lastMessageAt: chat.last_message_at,
    messageCount: chat.message_count,
    unreadCount: chat.unread_count || 0
  };
};

// Format message for display
export const formatMessageForDisplay = (message, currentUserID) => {
  return {
    messageID: message.message_id,
    senderID: message.sender_id,
    timestamp: message.timestamp,
    contentEncryptedForRecipient: message.content_encrypted_for_recipient,
    contentEncryptedForSender: message.content_encrypted_for_sender,
    messageType: message.message_type,
    isRead: message.is_read,
    isFromCurrentUser: message.sender_id === currentUserID
  };
};

// Mark messages as read
export const markMessagesAsRead = async (chatID, userKeys) => {
  try {
    const response = await apiRequest(`/api/v1/private-chats/${chatID}/messages/read`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
    }, userKeys);

    if (!response.success) {
      throw new Error(response.error || 'Failed to mark messages as read');
    }

    return response;
  } catch (error) {
    throw error;
  }
};
